import { useMutation, useQueryClient } from 'react-query';

import useApiHelper from '../../hooks/useApiHelper';
import { applianceQueryKeys, planetDeviceQueryKeys } from '../QueryKeys';
import { useDefaultMutateResponseHandlers } from '../../hooks/useDefaultMutateResponeHandlers';
import {
  IPlanetDevice,
  IPlanetDeviceUpdateRequest,
} from '../../types/PlanetDevice/PlanetDevice';
import { IDefaultResponseWithData } from '../../types/system/DefaultResponse';

export default function usePlanetDeviceMutations() {
  const queryClient = useQueryClient();
  const { put, del, post } = useApiHelper();
  const { handleSuccess, handleError } =
    useDefaultMutateResponseHandlers('Planet Device');

  const updatePlanetDevice = useMutation({
    mutationFn: (device: IPlanetDeviceUpdateRequest) =>
      put<IPlanetDeviceUpdateRequest, IDefaultResponseWithData<IPlanetDevice>>(
        `/v1/device/${device.id}`,
        device
      ),
    onError: (error) => handleError(error, 'update'),
    onSuccess: (data) => handleSuccess(data, 'update'),
    onSettled: () => {
      queryClient.invalidateQueries(planetDeviceQueryKeys.all());
      queryClient.invalidateQueries(applianceQueryKeys.all());
    },
  });

  const setupPlanetDevice = useMutation({
    mutationFn: (devices: { devices: string[] }) =>
      post(`/v1/device/setup`, devices),
    onError: (error) =>
      handleError(error, 'setup', 'Failed to setup device', true),
    onSuccess: (data) =>
      handleSuccess(data, 'setup', 'Successfully setup device'),
  });

  const delPlanetDevice = useMutation({
    mutationFn: (device: IPlanetDevice) => del(`/v1/device/${device.id}`),
    onError: (error) => handleError(error, 'delete'),
    onSuccess: (data) => handleSuccess(data, 'delete'),
    onSettled: () => {
      queryClient.invalidateQueries(planetDeviceQueryKeys.all());
      queryClient.invalidateQueries(applianceQueryKeys.all());
    },
  });

  return {
    updatePlanetDevice,
    setupPlanetDevice,
    delPlanetDevice,
  };
}
